import {
  MergerAffectedUsersResponse,
  MergerStorageImpactResponse,
} from 'src/components/adminDashboard/types';
import { UserRole } from 'src/types';

import { MergeUserPreview, OrgOption, StorageStats } from './types';

const VALID_USER_ROLES: UserRole[] = [
  'SUPER_ADMIN',
  'ADMIN',
  'SUPPORT',
  'BASIC',
];

export const normalizeUserRole = (role: unknown): UserRole => {
  if (typeof role !== 'string') {
    return 'BASIC';
  }

  const normalizedRole = role.trim().toUpperCase();

  return VALID_USER_ROLES.includes(normalizedRole as UserRole)
    ? (normalizedRole as UserRole)
    : 'BASIC';
};

export const getOriginalUserRole = (
  user: MergerAffectedUsersResponse['target']['users'][number]
): UserRole =>
  normalizeUserRole(
    user.original_user_role ?? user.role ?? user.merged_user_role
  );

export const getFinalUserRole = (
  user: MergerAffectedUsersResponse['target']['users'][number]
): UserRole => normalizeUserRole(user.merged_user_role ?? user.role);

export function getValidationError(
  survivor: OrgOption | null,
  sources: OrgOption[]
): string | null {
  if (!survivor) {
    return 'Please select a survivor organization.';
  }

  if (sources.length === 0) {
    return 'Please select at least one source organization.';
  }

  const seen = new Set<string>();
  const hasDuplicate = sources.some((source) => {
    const key = source.data.org_id.toString();

    if (seen.has(key)) {
      return true;
    }

    seen.add(key);
    return false;
  });

  if (hasDuplicate) {
    return 'Duplicate source organizations are not allowed.';
  }

  if (sources.some((source) => source.data.org_id === survivor.data.org_id)) {
    return 'Survivor organization cannot also be a source.';
  }

  return null;
}

export function buildRowsFromResponse(
  response: MergerAffectedUsersResponse
): MergeUserPreview[] {
  const targetRows = response.target.users.map((user) => ({
    email: user.email,
    origin: response.target.org_name,
    originRole: getOriginalUserRole(user),
    finalRole: getFinalUserRole(user),
  }));

  const sourceRows = response.sources.flatMap((source) =>
    source.users.map((user) => ({
      email: user.email,
      origin: source.org_name,
      originRole: getOriginalUserRole(user),
      finalRole: getFinalUserRole(user),
    }))
  );

  return [...targetRows, ...sourceRows];
}

export function buildStorageStatsFromImpact(
  response: MergerStorageImpactResponse,
  newStorageLimit: number
): StorageStats {
  const survivorUsedGb = response.target.storage_used;
  const incomingGb = response.sources.reduce(
    (sum, source) => sum + source.storage_used,
    0
  );
  const totalRequiredGb = survivorUsedGb + incomingGb;
  const basePlanLimitGb = Math.max(
    response.target.storage_limit,
    ...response.sources.map((source) => source.storage_limit)
  );
  const sanitizedNewStorageLimit = Math.max(newStorageLimit || 0, 0);
  const denominator =
    sanitizedNewStorageLimit > 0 ? sanitizedNewStorageLimit : basePlanLimitGb;
  const usedPercent =
    denominator > 0 ? (totalRequiredGb / denominator) * 100 : 0;
  const riskCapacity =
    sanitizedNewStorageLimit > 0 ? sanitizedNewStorageLimit : 0;

  return {
    newStorageLimit: sanitizedNewStorageLimit,
    survivorUsedGb,
    incomingGb,
    totalRequiredGb,
    basePlanLimitGb,
    usedPercent,
    isReadOnlyRisk: totalRequiredGb > riskCapacity,
  };
}

export function computeInitialStorageOverride(
  response: MergerStorageImpactResponse
): number {
  const maxLimit = Math.max(
    response.target.storage_limit,
    ...response.sources.map((s) => s.storage_limit)
  );
  const totalRequired =
    response.target.storage_used +
    response.sources.reduce((sum, s) => sum + s.storage_used, 0);

  return Math.max(maxLimit, totalRequired);
}

const DECIMAL_GB_IN_BYTES = 1000 ** 3;

export const convertBytesToDecimalGb = (
  bytes: number,
  decimals = 3
): number => {
  if (bytes <= 0) return 0;

  const dm = decimals < 0 ? 0 : decimals;
  const gb = bytes / DECIMAL_GB_IN_BYTES;

  return parseFloat(gb.toFixed(dm));
};

export const convertDecimalGbToBytes = (gb: number): number => {
  if (gb <= 0) return 0;

  return Math.round(gb * DECIMAL_GB_IN_BYTES);
};

export const formatBytesToDecimalGb = (bytes: number, decimals = 3): string =>
  `${convertBytesToDecimalGb(bytes, decimals)}`;
